import React, { useState } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import AuthShell from '../components/auth/AuthShell';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';

const ResetPassword = () => {
  const { resetPassword } = useAuth();
  const { toast } = useToast();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') || '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast({ title: 'Password too short', description: 'Use at least 6 characters.' });
    if (password !== confirm) return toast({ title: 'Passwords do not match' });
    setLoading(true);
    try {
      const res = await resetPassword(token, password);
      toast({ title: 'Password updated', description: res.message });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 1800);
    } catch (err) {
      toast({ title: 'Could not reset password', description: err?.response?.data?.detail || 'The link may have expired.' });
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell title="Invalid link" subtitle="This password reset link is missing its token."
        footer={<Link to="/forgot-password" className="text-mahogany font-medium hover:underline">Request a new link</Link>}>
        <div className="p-6 rounded-2xl bg-rose-soft/60 border border-rose/60 text-mahogany/80 text-sm">
          Please use the link from your email, or request a new one.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Set a New Password"
      subtitle="Choose a new password for your account."
      footer={<Link to="/login" className="text-mahogany font-medium hover:underline">Back to sign in</Link>}
    >
      {done ? (
        <div className="p-6 rounded-2xl bg-rose-soft/60 border border-rose/60">
          <p className="font-serif-display text-xl text-burgundy">All set!</p>
          <p className="mt-2 text-mahogany/80 text-sm">Your password has been updated. Redirecting you to sign in…</p>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">New Password</span>
            <input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-mahogany/30 pb-3 pt-1 text-burgundy focus:outline-none focus:border-mahogany transition-colors" />
          </label>
          <label className="block">
            <span className="block text-xs uppercase tracking-[0.2em] text-mahogany/80 mb-2">Confirm Password</span>
            <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
              className="w-full bg-transparent border-b border-mahogany/30 pb-3 pt-1 text-burgundy focus:outline-none focus:border-mahogany transition-colors" />
          </label>
          <button type="submit" disabled={loading} className="pill-btn justify-center w-full">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Update Password
          </button>
        </form>
      )}
    </AuthShell>
  );
};

export default ResetPassword;
